import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { projects, SECTEURS } from "../data/projects";
import Seo from "../components/Seo";

function Card({ project }) {
  return (
    <motion.div
      layout
      data-testid={`portfolio-card-${project.id}`}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
      className="group"
    >
      <Link
        to={project.route}
        data-cursor="view"
        className="block relative overflow-hidden rounded-2xl aspect-[4/5] bg-zinc-900 border border-white/[0.06]"
        aria-label={`Voir le projet ${project.title}`}
      >
        <img
          src={project.image}
          alt={project.title}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-transparent transition-opacity duration-500 group-hover:opacity-95" />

        <div className="absolute top-4 left-4 right-4 flex items-start justify-between font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-300">
          <span className="px-2.5 py-1 rounded-full border border-white/15 bg-white/[0.04] backdrop-blur">
            {project.label}
          </span>
          <span>{project.year}</span>
        </div>

        <div className="absolute bottom-5 left-5 right-5">
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--accent)] mb-2 block">
            {project.type}
          </span>
          <h3 className="font-display text-3xl sm:text-4xl tracking-tighter text-white leading-none mb-3">
            {project.title}
          </h3>
          <div className="flex flex-wrap gap-2">
            {project.tags.map((t) => (
              <span
                key={t}
                className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-300 px-2.5 py-1 rounded-full border border-white/15 bg-white/[0.04] backdrop-blur"
              >
                {t}
              </span>
            ))}
          </div>
        </div>

        <span className="absolute top-1/2 right-5 -translate-y-1/2 w-12 h-12 rounded-full bg-[var(--accent)] text-black hidden sm:flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:rotate-45">
          <ArrowUpRight className="w-5 h-5" strokeWidth={2} />
        </span>
      </Link>
    </motion.div>
  );
}

export default function Portfolio() {
  const [filter, setFilter] = useState("tous");
  const filtered =
    filter === "tous" ? projects : projects.filter((p) => p.secteur === filter);

  return (
    <>
      <Seo title="Portfolio — nos réalisations" description="Découvrez des exemples de sites créés par ML. Agence Web : restauration, beauté, bien-être, artisans et services de proximité." />
      <Navigation />
      <main data-testid="portfolio-page" className="relative z-10 min-h-screen">
        {/* Header */}
        <section className="px-5 sm:px-8 lg:px-12 pt-36 sm:pt-44 pb-14">
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-500 mb-6 block">
            Portfolio — Réalisations & concepts
          </span>
          <h1 className="font-display uppercase text-6xl sm:text-7xl lg:text-8xl leading-[0.88] tracking-tighter max-w-4xl">
            Des commerces ordinaires, <span className="text-outline">une présence</span> extraordinaire.
          </h1>
          <p className="mt-8 max-w-xl text-zinc-400 text-base sm:text-lg leading-relaxed">
            Une sélection de sites immersifs signés ML. Chaque projet est un concept pensé pour montrer ce qu'on peut faire pour un commerce comme le vôtre.
          </p>
        </section>

        {/* Filters */}
        <section className="px-5 sm:px-8 lg:px-12">
          <div className="flex flex-wrap gap-2.5 border-b border-white/[0.08] pb-8">
            {SECTEURS.map((s) => {
              const active = filter === s.id;
              return (
                <button
                  key={s.id}
                  data-testid={`portfolio-filter-${s.id}`}
                  onClick={() => setFilter(s.id)}
                  className={`font-mono text-[11px] uppercase tracking-[0.18em] px-4 py-2.5 rounded-full border transition-all duration-300 ${
                    active
                      ? "bg-[var(--accent)] text-black border-[var(--accent)]"
                      : "border-white/[0.12] text-zinc-400 hover:text-white hover:border-white/30"
                  }`}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </section>

        {/* Grid */}
        <section className="px-5 sm:px-8 lg:px-12 py-14">
          <motion.div
            layout
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            <AnimatePresence mode="popLayout">
              {filtered.map((p) => (
                <Card key={p.id} project={p} />
              ))}
            </AnimatePresence>
          </motion.div>

          {filtered.length === 0 && (
            <p className="text-center text-zinc-500 py-20 font-mono text-sm uppercase tracking-[0.2em]">
              De nouveaux concepts arrivent pour ce secteur.
            </p>
          )}
        </section>

        {/* CTA bas de page */}
        <section className="px-5 sm:px-8 lg:px-12 pb-28">
          <div className="tracing-card bento p-10 sm:p-16 text-center">
            <h2 className="font-display uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.92] tracking-tighter mb-6">
              Vous ne voyez pas votre secteur ?
            </h2>
            <p className="max-w-xl mx-auto text-zinc-400 leading-relaxed mb-10">
              Ou vous voulez voir ce qu'on ferait pour <span className="text-white">vous</span>, spécifiquement ? Recevez un aperçu personnalisé de votre commerce, offert et sans engagement.
            </p>
            <Link
              to="/mockup-gratuit"
              data-testid="portfolio-cta-mockup"
              className="btn-pill btn-pill-accent"
            >
              Voir ce qu'on ferait pour mon commerce
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
